import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { fetchOsDevices, fetchReport } from '@/api/reports'
import { ColumnPicker } from '@/components/ColumnPicker'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { HorizontalScrollArea } from '@/components/HorizontalScrollArea'
import { ReportTable } from '@/components/ReportTable'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getReportColumns } from '@/config/columns'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { shortenOsLabel } from '@/lib/chartLabels'
import { cn } from '@/lib/utils'

export function OsBreakdownReport({ customerIds, onExportNote }) {
  const [summary, setSummary] = useState([])
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [selectedOs, setSelectedOs] = useState(null)
  const [details, setDetails] = useState([])
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [error, setError] = useState('')

  const detailColumns = getReportColumns('asset-list')
  const {
    visibleIds,
    visibleColumns,
    toggleColumn,
    showAll,
    resetDefaults,
  } = useColumnVisibility('report-columns-os-devices', detailColumns)

  const chartData = useMemo(
    () =>
      summary.map((row) => ({
        label: row.os_name || 'Unknown',
        value: row.count || 0,
      })),
    [summary],
  )

  useEffect(() => {
    setSummaryLoading(true)
    setError('')
    setSelectedOs(null)
    fetchReport('os-breakdown', {}, customerIds)
      .then((data) => setSummary(Array.isArray(data) ? data : []))
      .catch(() => {
        setError('Could not load OS breakdown.')
        setSummary([])
      })
      .finally(() => setSummaryLoading(false))
  }, [customerIds])

  useEffect(() => {
    if (!selectedOs) {
      setDetails([])
      return
    }
    setDetailsLoading(true)
    fetchOsDevices(selectedOs, customerIds)
      .then((data) => setDetails(Array.isArray(data) ? data : []))
      .catch(() => setError('Could not load devices for this operating system.'))
      .finally(() => setDetailsLoading(false))
  }, [selectedOs, customerIds])

  useEffect(() => {
    onExportNote?.(
      'CSV export lists the OS summary counts. Use the table below to drill into devices, then export the asset register for a full device list.',
    )
  }, [onExportNote])

  function toggleOs(osName) {
    setSelectedOs((current) => (current === osName ? null : osName))
  }

  if (summaryLoading) {
    return <Skeleton className="h-48 w-full" />
  }

  const totalDevices = summary.reduce((n, row) => n + (row.count || 0), 0)

  return (
    <div className="space-y-6">
      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <p className="text-sm text-muted-foreground">
        Operating system versions across the estate. Click an OS in the table or chart to list
        matching devices. Total devices in scope: {totalDevices.toLocaleString('en-GB')}.
      </p>

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <HorizontalScrollArea>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Operating system</th>
                <th className="px-4 py-3 font-medium">Device count</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                    No device data. Run a sync from Admin first.
                  </td>
                </tr>
              ) : (
                summary.map((row) => {
                  const osName = row.os_name || 'Unknown'
                  const isSelected = selectedOs === osName
                  return (
                    <tr
                      key={osName}
                      className={cn(
                        'cursor-pointer border-b border-border/60 transition-colors hover:bg-muted/30',
                        isSelected && 'bg-primary/10',
                      )}
                      onClick={() => toggleOs(osName)}
                    >
                      <td className="px-4 py-3 font-medium" title={osName}>
                        {osName}
                      </td>
                      <td className="px-4 py-3">{row.count?.toLocaleString('en-GB')}</td>
                      <td className="px-4 py-3 text-xs text-primary">
                        {isSelected ? 'Hide devices' : 'View devices'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </HorizontalScrollArea>

        <div className="min-h-[240px] rounded-lg border border-border bg-card p-4">
          <SimplePieChart
            data={chartData}
            formatLabel={shortenOsLabel}
            showLegend={false}
            activeLabel={selectedOs}
            onItemClick={toggleOs}
            emptyMessage="No OS data"
          />
        </div>
      </div>

      {selectedOs ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-base font-semibold">
              {selectedOs}
              {details.length > 0 ? (
                <span className="ml-2 font-normal text-muted-foreground">
                  ({details.length.toLocaleString('en-GB')} devices)
                </span>
              ) : null}
            </h3>
            <div className="flex items-center gap-2">
              {detailColumns.length > 0 ? (
                <ColumnPicker
                  columns={detailColumns}
                  visibleIds={visibleIds}
                  onToggle={toggleColumn}
                  onShowAll={showAll}
                  onResetDefaults={resetDefaults}
                />
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => setSelectedOs(null)}>
                <X className="h-4 w-4" />
                Close
              </Button>
            </div>
          </div>
          {detailsLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <ReportTable
              key={selectedOs || 'os-devices'}
              rows={details}
              visibleColumns={visibleColumns}
              defaultSort={{ id: 'hostname', dir: 'asc' }}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}
